import { Button, FileDroppableArea, MagellanPopup } from 'components/atoms';
import { useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { PiBroom, PiFileCsvDuotone, PiUploadSimple } from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { usePostSpotifyWatchlistImport } from 'services/spotifyWatchlist';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { readableFileSize } from 'utils/components/inputs/file';
import './PopupWatchlistImport.scss';

interface IPopupWatchlistImportProps {
  trigger?: JSX.Element;
  onSuccess?: () => void;
}

const getFileParts = (file: File) => {
  const lastDot = file.name.lastIndexOf('.');

  if (lastDot === -1) {
    return { name: file.name, extension: '' };
  }

  return {
    name: file.name.slice(0, lastDot),
    extension: file.name.slice(lastDot + 1),
  };
};

const PopupWatchlistImport = ({
  trigger,
  onSuccess,
}: IPopupWatchlistImportProps) => {
  const popupRef = useRef<PopupActions>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dropzoneKey, setDropzoneKey] = useState(0);
  const { t } = useTranslation();
  const importMutation = usePostSpotifyWatchlistImport();

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    multiple: false,
    noClick: true,
    onDrop: acceptedFiles => {
      const nextFile = acceptedFiles[0];

      if (nextFile) {
        setFile(nextFile);
      }
    },
    onDropRejected: () => {
      setFile(null);
      setDropzoneKey(prev => prev + 1);
    },
  });

  const fileParts = useMemo(
    () => (file ? getFileParts(file) : null),
    [file]
  );

  const onClose = () => {
    setFile(null);
    setDropzoneKey(prev => prev + 1);
  };

  const onClearFile = () => {
    setFile(null);
    setDropzoneKey(prev => prev + 1);
  };

  const onConfirmAndSend = async () => {
    if (!file) {
      return;
    }

    await importMutation.mutateAsync(
      { file },
      {
        onSuccess: () => {
          popupRef.current?.close();
          onSuccess?.();
        },
      }
    );
  };

  return (
    <Popup ref={popupRef} modal nested trigger={trigger} onClose={onClose}>
      <MagellanPopup
        {...getRootProps({ key: dropzoneKey })}
        className='p-5 rounded-md watchlist-import-popup'>
        <FileDroppableArea visible={isDragActive} />

        <MagellanPopup.Header className='justify-center w-80 mb-5'>
          <PiUploadSimple /> {t('spotifyWatchlist.popup.header')}
        </MagellanPopup.Header>

        <div className='flex flex-col gap-3 w-80'>
          {!file && (
            <>
              <Button type='button' fullWidth onClick={open}>
                <Button.Icon icon={<PiFileCsvDuotone />} />
                <Button.Text text={t('button.addFile')} />
              </Button>
              <input {...getInputProps()} className='hidden' />
            </>
          )}

          {file && fileParts && (
            <div className='file-preview-wrapper'>
              <p className='file-preview-title'>
                {t('spotifyWatchlist.popup.file')}
              </p>
              <div className='file-preview'>
                <p className='file-preview-name' title={fileParts.name}>
                  {fileParts.name}
                </p>
                {fileParts.extension && (
                  <p
                    className='file-preview-extension'
                    title={`.${fileParts.extension}`}>
                    .{fileParts.extension}
                  </p>
                )}
              </div>
              <p className='file-preview-size'>
                {t('spotifyWatchlist.popup.fileSize')}:{' '}
                {readableFileSize(file.size)}
              </p>
            </div>
          )}

          <MagellanPopup.BottomButtons>
            {file && (
              <Button isClear onlyIcon onClick={onClearFile}>
                <Button.Icon icon={<PiBroom />} />
              </Button>
            )}
            <Button
              type='button'
              variant={BUTTON_VARIANT.submit}
              disabled={!file}
              isLoading={importMutation.isPending}
              onClick={onConfirmAndSend}>
              <Button.Text text={t('spotifyWatchlist.popup.confirmAndSend')} />
              <Button.Icon icon={<PiUploadSimple />} />
            </Button>
          </MagellanPopup.BottomButtons>
        </div>
      </MagellanPopup>
    </Popup>
  );
};

export default PopupWatchlistImport;
